import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

async function apiFetch(path, opts = {}) {
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg = typeof detail === "string" ? detail
      : Array.isArray(detail) ? detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ")
      : detail?.msg ? detail.msg
      : res.statusText || "Request failed";
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch("/api/auth/me");
      setUser(data.user);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // No need to skip /me anymore — Emergent's hash-based callback flow is gone.
    // @react-oauth/google uses a popup so /me runs normally on initial load.
    refresh();
  }, [refresh]);

  const signup = useCallback(async ({ name, email, password, phone, marketing_opt_in }) => {
    return apiFetch("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({ name, email, password, phone, marketing_opt_in }),
    });
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const data = await apiFetch("/api/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
    setUser(data.user);
    return data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    return apiFetch("/api/auth/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const data = await apiFetch("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // ignore
    }
    setUser(null);
  }, []);

  const googleCallback = useCallback(async ({ code, redirect_uri }) => {
    const data = await apiFetch("/api/auth/google", {
      method: "POST",
      body: JSON.stringify(redirect_uri ? { code, redirect_uri } : { code }),
    });
    setUser(data.user);
    return data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    return apiFetch("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }, []);

  const resetPassword = useCallback(async ({ token, password }) => {
    return apiFetch("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  }, []);

  const updateProfile = useCallback(async ({ name, phone }) => {
    const data = await apiFetch("/api/account/profile", {
      method: "PATCH",
      body: JSON.stringify({ name, phone }),
    });
    setUser(data.user);
    return data;
  }, []);

  const changeEmail = useCallback(async ({ email, password }) => {
    const data = await apiFetch("/api/account/change-email", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    await refresh();
    return data;
  }, [refresh]);

  const changePassword = useCallback(async ({ current_password, new_password }) => {
    return apiFetch("/api/account/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    });
  }, []);

  const value = useMemo(() => ({
    user, loading, refresh,
    signup, verifyEmail, resendVerification,
    login, logout, googleCallback,
    forgotPassword, resetPassword,
    updateProfile, changeEmail, changePassword,
    apiFetch,
  }), [user, loading, refresh, signup, verifyEmail, resendVerification, login, logout, googleCallback, forgotPassword, resetPassword, updateProfile, changeEmail, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
